import {
  Button,
  Dropdown,
  DropdownMenuItemProps,
  Icons,
} from '@commenda/kakashi';
import { IBusinessEntity } from '@commenda/types';
import { SelectEntityDropdownNew } from '@commenda/ui';
import { cn } from '@commenda/utils';
import React from 'react';
import { BackButton } from './BackButton';

export enum CommendaAppSurfaceArea {
  Overview = 'OVERVIEW',
  Corporations = 'CORPORATIONS',
  Settings = 'SETTINGS',
  GlobalIndirectTax = 'GLOBAL_INDIRECT_TAX',
  TaxAndReporting = 'TAX_AND_REPORTING',
  Accounting = 'ACCOUNTING',
  TransferPricing = 'TRANSFER_PRICING',
  Services = 'SERVICES',
  Documents = 'DOCUMENTS',
  Registrations = 'REGISTRATIONS',
  Transactions = 'TRANSACTIONS',
  Exemptions = 'EXEMPTIONS',
  Filings = 'FILINGS',
  Calculations = 'CALCULATIONS',
  Policies = 'POLICIES',
  Benchmarking = 'BENCHMARKING',
  AddressValidation = 'ADDRESS_VALIDATION',
  TeamMembers = 'TEAM_MEMBERS',
  Onboarding = 'ONBOARDING',
  Clients = 'CLIENTS',
}

export const APP_DISPLAY_NAME: Record<CommendaAppSurfaceArea, string> = {
  [CommendaAppSurfaceArea.Overview]: 'Overview',
  [CommendaAppSurfaceArea.Corporations]: 'Entities',
  [CommendaAppSurfaceArea.Settings]: 'Settings',
  [CommendaAppSurfaceArea.GlobalIndirectTax]: 'Global Indirect Tax',
  [CommendaAppSurfaceArea.TaxAndReporting]: 'Tax & Reporting',
  [CommendaAppSurfaceArea.Accounting]: 'Accounting',
  [CommendaAppSurfaceArea.TransferPricing]: 'Transfer Pricing',
  [CommendaAppSurfaceArea.Services]: 'Services',
  [CommendaAppSurfaceArea.Documents]: 'Documents',
  [CommendaAppSurfaceArea.Registrations]: 'Registrations',
  [CommendaAppSurfaceArea.Transactions]: 'Transactions',
  [CommendaAppSurfaceArea.Exemptions]: 'Customers',
  [CommendaAppSurfaceArea.Filings]: 'Filings',
  [CommendaAppSurfaceArea.Calculations]: 'Calculations',
  [CommendaAppSurfaceArea.Policies]: 'Policy Agreements',
  [CommendaAppSurfaceArea.Benchmarking]: 'Benchmarking',
  [CommendaAppSurfaceArea.AddressValidation]: 'Address Validation',
  [CommendaAppSurfaceArea.TeamMembers]: 'Team Members',
  [CommendaAppSurfaceArea.Onboarding]: 'Onboarding',
  [CommendaAppSurfaceArea.Clients]: 'Clients',
};

function resolveAppDisplayName(app: CommendaAppSurfaceArea | string): string {
  return APP_DISPLAY_NAME[app as CommendaAppSurfaceArea] ?? app;
}

interface PageHeaderProps {
  currentApp: CommendaAppSurfaceArea | string;
  pageTitle: string;
  onBack?: () => void;
  backLabel?: string;
  showEntitySelector?: boolean;
  companyId?: number | null;
  selectedEntityId?: number | null;
  onSelectEntity?: (entityId: number) => void;
  filterEntity?: (entity: IBusinessEntity) => boolean;
  showAllEntities?: boolean;
  showAddEntity?: boolean;
  hideEntitySelectorChevron?: boolean;
  showActions?: boolean;
  menuItems?: DropdownMenuItemProps[];
  actionLabel?: string;
  onAction?: () => void;
  trailing?: React.ReactNode;
  customActions?: React.ReactNode;
  compact?: boolean;
}

export const PageHeader = ({
  currentApp,
  pageTitle,
  onBack,
  backLabel,
  showEntitySelector = false,
  companyId,
  selectedEntityId,
  onSelectEntity,
  filterEntity,
  showAllEntities = false,
  showAddEntity = true,
  hideEntitySelectorChevron = false,
  showActions = true,
  menuItems = [],
  actionLabel = 'Add',
  onAction,
  trailing,
  customActions,
  compact = false,
}: PageHeaderProps) => {
  const showHeadingRow = !!pageTitle;
  const appLabel = resolveAppDisplayName(currentApp);
  const showEntityDropdown =
    showEntitySelector && selectedEntityId && companyId && onSelectEntity;

  return (
    <div className="flex items-end justify-between">
      <div className={`flex flex-col${compact ? ' gap-0' : ' gap-xs'}`}>
        {onBack ? (
          <BackButton label={backLabel ?? appLabel} onClick={onBack} />
        ) : (
          <span className="txt-extra-small-bold text-fg-brand">{appLabel}</span>
        )}

        {showHeadingRow && (
          <div className="flex items-center gap-xl">
            <h1
              className={cn('display-xs-bold', !showEntityDropdown && 'py-xs')}
            >
              {pageTitle}
            </h1>

            {showEntityDropdown && (
              <SelectEntityDropdownNew
                companyId={companyId}
                selectedEntityId={selectedEntityId}
                onSelectEntity={onSelectEntity}
                filter={filterEntity}
                showAddEntity={showAddEntity}
                showAllEntities={showAllEntities}
                hideChevron={hideEntitySelectorChevron}
              />
            )}

            {trailing}
          </div>
        )}
      </div>

      {customActions ? (
        <div className="flex items-center gap-md">{customActions}</div>
      ) : (
        showActions && (
          <div className="flex items-center gap-md">
            {menuItems.length > 0 && (
              <Dropdown align="end" menu={{ groups: [{ items: menuItems }] }}>
                <div className="flex size-8 cursor-pointer items-center justify-center rounded-md transition hover:bg-bg-primary group">
                  <Icons.DotsVertical className="size-4 text-button-tertiary-fg group-hover:text-button-tertiary-fg-hover" />
                </div>
              </Dropdown>
            )}

            {onAction && (
              <Button
                variant="primary"
                size="md"
                leftIcon={<Icons.Plus className="size-4" />}
                onClick={onAction}
              >
                {actionLabel}
              </Button>
            )}
          </div>
        )
      )}
    </div>
  );
};
